import { Link } from "react-router-dom";
import { useState } from "react";

const INITIAL_FORM = { name: "", email: "", subject: "", message: "" };

const contactDetails = [
  { label: "Email", value: "business@nexorasventures.com" },
  { label: "Phone", value: "+971 0528 26 5243" },
  { label: "Location", value: "Sharjah, Media City, UAE" },
  { label: "Hours", value: "Mon-Fri, 9am-6pm GST" },
];

const paymentNotes = [
  "All project prices and payment amounts are quoted in AED unless otherwise agreed in writing.",
  "VAT, bank fees, gateway charges, or other applicable taxes will be disclosed before payment confirmation.",
  "Refunds, where approved, are processed only through the original mode of payment.",
];

export default function ContactForm() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    try {
      const apiUrl = process.env.NODE_ENV === "production"
        ? "/api/contact"
        : "http://localhost:5000/api/contact";

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus("success");
        setForm(INITIAL_FORM);
      } else {
        setStatus("error");
        if (response.status === 503) {
          setErrorMsg("Contact service is temporarily unavailable. Please email business@nexorasventures.com.");
        } else {
          setErrorMsg(data.error || "Something went wrong. Please try again.");
        }
      }
    } catch (error) {
      setStatus("error");
      setErrorMsg("Could not connect to server. Make sure backend is running.");
    }
  };

  return (
    <section id="contact" className="bg-slate-50 py-28">
      <div className="mx-auto max-w-6xl px-6">
        <div className="grid items-start gap-16 md:grid-cols-2">
          <div className="animate-fade-in space-y-6">
            <span className="text-sm font-semibold uppercase tracking-wide text-blue-600">
              Get In Touch
            </span>
            <h2 className="mt-2 mb-4 text-3xl font-extrabold text-slate-900 md:text-4xl">
              Let&apos;s build something together
            </h2>
            <p className="text-lg leading-relaxed text-slate-500">
              Have a project in mind? Share your requirements and we&apos;ll respond with scope guidance, timeline expectations, and next-step payment details.
            </p>

            <div className="space-y-4 rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
              {contactDetails.map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="mt-1 flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-sm font-bold text-blue-700">
                    {item.label.slice(0, 1)}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-400">{item.label}</p>
                    <p className="text-sm font-semibold text-slate-900">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-[2rem] border border-amber-200 bg-amber-50 p-6 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">
                Payment & Compliance Notice
              </p>
              <div className="mt-3 space-y-3 text-sm leading-7 text-amber-950">
                {paymentNotes.map((note) => (
                  <p key={note}>{note}</p>
                ))}
                <p>
                  Review our{" "}
                  <Link to="/pricing" className="font-semibold underline decoration-amber-400 underline-offset-4">
                    Pricing page
                  </Link>{" "}
                  and{" "}
                  <Link to="/terms" className="font-semibold underline decoration-amber-400 underline-offset-4">
                    Terms & Conditions
                  </Link>{" "}
                  before making payment arrangements.
                </p>
              </div>
            </div>
          </div>

          <div className="animate-fade-in animate-fade-in-delay-100 rounded-[2rem] border border-slate-200 bg-white p-8 shadow-2xl">
            <h3 className="mb-6 text-xl font-bold text-slate-900">Send us a message</h3>

            {status === "success" && (
              <div className="mb-6 rounded-3xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-700 shadow-sm">
                <p className="font-semibold">Message sent successfully.</p>
                <p className="text-sm">We&apos;ll reply within 24 hours.</p>
              </div>
            )}

            {status === "error" && (
              <div className="mb-6 rounded-3xl border border-red-200 bg-red-50 p-4 text-red-700 shadow-sm">
                <p className="font-semibold">Unable to send your message.</p>
                <p className="text-sm">{errorMsg}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                    className="w-full rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                    className="w-full rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="What is this about?"
                  required
                  className="w-full rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project..."
                  required
                  rows={6}
                  className="w-full resize-none rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                />
              </div>

              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex items-center justify-center gap-3 rounded-3xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-200 transition-transform hover:-translate-y-1 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "loading" ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Sending...
                  </>
                ) : (
                  "Send Message"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
